import { useState, useRef, useEffect } from 'react';
import { Figure } from '../types';
import Button from './ui/Button';
import Input from './ui/Input';
import Card from './ui/Card';

interface FigureEditorProps {
  figure: Figure;
  originalImageUrl?: string;
  onUpdate: (figure: Figure) => void;
  onDelete?: () => void;
}

export default function FigureEditor({ figure, originalImageUrl, onUpdate, onDelete }: FigureEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [cropBox, setCropBox] = useState(figure.boundingBox || { x: 0, y: 0, width: 100, height: 100 });

  useEffect(() => {
    if (canvasRef.current && originalImageUrl) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.crossOrigin = 'anonymous'; // Allow loading from Cloud Storage
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Draw crop box
        if (figure.boundingBox) {
          const box = figure.boundingBox;
          const x = (box.x / 100) * img.width;
          const y = (box.y / 100) * img.height;
          const width = (box.width / 100) * img.width;
          const height = (box.height / 100) * img.height;

          ctx.strokeStyle = '#4CAF50';
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, width, height);
        }
      };
      img.src = originalImageUrl;
    }
  }, [originalImageUrl, figure.boundingBox]);

  const handleCrop = () => {
    if (canvasRef.current && originalImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Allow loading from Cloud Storage
      img.onload = () => {
        const box = cropBox;
        const x = (box.x / 100) * img.width;
        const y = (box.y / 100) * img.height;
        const width = (box.width / 100) * img.width;
        const height = (box.height / 100) * img.height;

        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = width;
        croppedCanvas.height = height;
        const ctx = croppedCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
          const croppedImage = croppedCanvas.toDataURL('image/png');
          onUpdate({ ...figure, image: croppedImage, boundingBox: cropBox });
          setIsEditing(false);
        }
      };
      img.src = originalImageUrl;
    }
  };

  return (
    <Card>
      <div className="flex flex-col sm:flex-row justify-between items-start 
                      sm:items-center mb-4 gap-4">
        <h3 className="text-lg font-semibold text-neutral-900">
          {figure.description || 'Untitled Figure'}
        </h3>
        <div className="flex flex-wrap gap-2">
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="danger" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {isEditing && originalImageUrl ? (
        <div>
          <canvas
            ref={canvasRef}
            className="max-w-full border border-neutral-300 rounded-lg mb-4"
          />
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                X: {cropBox.x.toFixed(1)}%
              </label>
            <input
              type="range"
              min="0"
              max="100"
              value={cropBox.x}
              onChange={(e) => setCropBox({ ...cropBox, x: parseFloat(e.target.value) })}
                className="w-full"
            />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Y: {cropBox.y.toFixed(1)}%
              </label>
            <input
              type="range"
              min="0"
              max="100"
              value={cropBox.y}
              onChange={(e) => setCropBox({ ...cropBox, y: parseFloat(e.target.value) })}
                className="w-full"
            />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Width: {cropBox.width.toFixed(1)}%
              </label>
            <input
              type="range"
              min="0"
              max="100"
              value={cropBox.width}
              onChange={(e) => setCropBox({ ...cropBox, width: parseFloat(e.target.value) })}
                className="w-full"
            />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Height: {cropBox.height.toFixed(1)}%
              </label>
            <input
              type="range"
              min="0"
              max="100"
              value={cropBox.height}
              onChange={(e) => setCropBox({ ...cropBox, height: parseFloat(e.target.value) })}
                className="w-full"
            />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="success" onClick={handleCrop}>
              Apply Crop
            </Button>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        figure.image && (
          <img
            src={figure.image}
            alt={figure.description}
            className="max-w-full rounded-lg border border-neutral-300 mb-4"
          />
        )
      )}

      <Input
        label="Description"
          value={figure.description}
          onChange={(e) => onUpdate({ ...figure, description: e.target.value })}
        />
    </Card>
  );
}
